import { useEffect, useMemo, useState } from 'react'
import { useGetPDPurchaseOrders } from '@/services/hooks/relay/dynamic'
import { PDPurchaseOrder } from '@/types/po-match'
import { PURCHASE_ORDER_INFO_TYPE_ID } from '@/utils/info-types'
import { normalizePoNumber, poNumbersMatch } from './shared'

type UseMatchingPONumberPOOptions = {
  invoicePoNumber: string | null
  orgId: string
  orgCredentialTypeId: string
  purchaseOrders: PDPurchaseOrder[]
  isLoadingPOs: boolean
  /** POs the user has closed. Never pinned again, however they first got here. */
  dismissedPoIds: Set<string>
  onAddPurchaseOrder: (po: PDPurchaseOrder) => void
}

/**
 * Finds the open PO whose number matches the invoice's PO number and pins it
 * into the panel via `onAddPurchaseOrder`, so it shows up even when PO
 * matching found no line items for it (and it would otherwise never appear).
 *
 * Filters on `normalized_num`, which the relay normalizes on both sides before
 * comparing — the raw invoice value goes over the wire, so an invoice reading
 * `PO-00123` finds a PO stored as `123` and vice versa.
 */
export default function useMatchingPONumberPO({
  invoicePoNumber,
  orgId,
  orgCredentialTypeId,
  purchaseOrders,
  isLoadingPOs,
  dismissedPoIds,
  onAddPurchaseOrder,
}: UseMatchingPONumberPOOptions) {
  const rawNumber = invoicePoNumber?.trim() || ''

  // A number that reduces to nothing (`0`, `PO`) matches nothing, so don't ask.
  const canMatch = !!normalizePoNumber(invoicePoNumber)

  const alreadyPresent = purchaseOrders.some((po) =>
    poNumbersMatch(po.number, invoicePoNumber),
  )

  // Ids land here when the hook pins a PO — or when a matching PO shows up
  // in the panel some other way (e.g. through line item matches). Ids never
  // leave, so closing that PO can't re-pin it, and (via the size check
  // below) the lookup never runs again — a re-fetch could only find the PO
  // the user just removed, and its pending state would flash the panel
  // skeleton in the meantime.
  const [handledMatchingPoIds, setHandledMatchingPoIds] = useState<Set<string>>(
    () => new Set(),
  )

  useEffect(() => {
    const present = purchaseOrders.filter((po) =>
      poNumbersMatch(po.number, invoicePoNumber),
    )
    if (!present.length) return
    setHandledMatchingPoIds((prev) => {
      if (present.every((po) => prev.has(po.external_id))) return prev
      const next = new Set(prev)
      present.forEach((po) => next.add(po.external_id))
      return next
    })
  }, [purchaseOrders, invoicePoNumber])

  // useGetPDPurchaseOrders disables itself without an org and credential type,
  // and a disabled query reports isPending forever — so these have to gate
  // `enabled` too, or isLoadingMatchingPO would never clear for a project that
  // has no credential type configured.
  const enabled =
    canMatch &&
    !isLoadingPOs &&
    !alreadyPresent &&
    handledMatchingPoIds.size === 0 &&
    !!orgId &&
    !!orgCredentialTypeId

  const { pdPurchaseOrders: numberMatchedPOs, isPending } =
    useGetPDPurchaseOrders({
      infoTypeId: PURCHASE_ORDER_INFO_TYPE_ID,
      orgId,
      orgCredentialTypeId,
      filters: { normalized_num: rawNumber, status: 'open', limit: '20' },
      enabled,
    })

  const matchingPO = useMemo(
    () =>
      numberMatchedPOs.find(
        (po) =>
          poNumbersMatch(po.number, invoicePoNumber) &&
          !handledMatchingPoIds.has(po.external_id) &&
          !dismissedPoIds.has(po.external_id),
      ) ?? null,
    [numberMatchedPOs, invoicePoNumber, handledMatchingPoIds, dismissedPoIds],
  )

  useEffect(() => {
    if (!matchingPO) return
    setHandledMatchingPoIds((prev) => new Set(prev).add(matchingPO.external_id))
    onAddPurchaseOrder(matchingPO)
  }, [matchingPO, onAddPurchaseOrder])

  const isLoadingMatchingPO = enabled && isPending

  return { isLoadingMatchingPO }
}
